import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Check, AlertTriangle, DollarSign } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const PricingPage = () => {
  useCanonical('https://ecuflashservice.com/pricing');
  
  const mainServices = [
    {
      name: 'DPF Delete',
      price: 248,
      description: 'Diesel Particulate Filter removal',
      features: ['DPF regeneration disabled', 'Pressure sensors disabled', 'Temperature sensors disabled', 'All related DTCs removed', 'Checksum correction included'],
      popular: true
    },
    {
      name: 'EGR Delete',
      price: 50,
      description: 'Exhaust Gas Recirculation removal',
      features: ['EGR valve disabled', 'EGR cooler disabled', 'Position monitoring disabled', 'All related DTCs removed', 'Checksum correction included'],
      popular: false
    },
    {
      name: 'AdBlue/SCR Delete',
      price: 698,
      description: 'Selective Catalytic Reduction removal',
      features: ['AdBlue injection disabled', 'NOx sensors disabled', 'SCR monitoring disabled', 'Inducement removed', 'All related DTCs removed'],
      popular: false
    },
    {
      name: 'DTC Removal',
      price: 10,
      priceNote: 'Starting from',
      description: 'Diagnostic Trouble Code removal',
      features: ['1 DTC = $10', '2-6 DTCs = $20', '7+ DTCs = $30', 'Checksum +$5 if needed', '30-day free rework'],
      popular: false
    }
  ];

  const additionalServices = [
    { name: 'Speed Limiter Removal', price: 30 },
    { name: 'Start/Stop Disable', price: 40 },
    { name: 'Flaps/Swirl Delete', price: 40 },
    { name: 'Lambda/O2 Delete', price: 50 },
    { name: 'Catalyst Delete', price: 50 },
    { name: 'Hot Start Fix', price: 40 },
    { name: 'MAF Removal', price: 40 },
    { name: 'Immo Off', price: 70 },
    { name: 'Stage 1 Tune', price: 248 },
    { name: 'Stage 2 Tune', price: 248 },
    { name: 'Custom Tune', price: 'Quote' },
    { name: 'Checksum Correction', price: 5 },
  ];

  const comboPacks = [
    {
      name: 'DPF + EGR Combo',
      originalPrice: 298,
      price: 248,
      savings: 50,
      includes: ['DPF Delete', 'EGR Delete', 'All related DTCs']
    },
    {
      name: 'Full Emission Delete',
      originalPrice: 996,
      price: 896,
      savings: 100,
      includes: ['DPF Delete', 'EGR Delete', 'AdBlue Delete', 'All related DTCs']
    },
    {
      name: 'Performance Pack',
      originalPrice: 546,
      price: 446,
      savings: 100,
      includes: ['DPF Delete', 'EGR Delete', 'Stage 1 Tune']
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>ECU Tuning Prices | DPF Delete $248 | EGR $50 | DTC $10 | Price List 2026</title>
        <meta name="description" content="ECU tuning price list 2026. DPF delete from $248, EGR delete $50, AdBlue delete $698, DTC removal from $10. No hidden fees. Combo discounts available." />
        <meta name="keywords" content="ECU tuning prices, DPF delete cost, EGR delete price, AdBlue delete cost, chip tuning prices, remap cost" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/how-it-works" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">How It Works</Link>
              <Link to="/services" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">Services</Link>
              <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
                <span>Get Started</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-white/10 mb-6">
            <DollarSign className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#888]">Transparent Pricing</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">ECU Tuning</span>{' '}
            <span className="text-[#00F0FF]">Price List 2026</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto">
            Transparent pricing. No hidden fees. All prices include checksum correction and 30-day support.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-12">Core Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainServices.map((service, idx) => (
              <div 
                key={idx} 
                className={`relative bg-[#0A0A0A] border p-6 transition-all hover:border-[#00F0FF]/50 ${
                  service.popular ? 'border-[#00F0FF]/50' : 'border-white/10'
                }`}
              >
                {service.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00F0FF] text-black text-xs font-bold uppercase tracking-wider px-3 py-1">
                    Most Popular
                  </div>
                )}
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-white mb-2">{service.name}</h3>
                <p className="text-[#666] text-sm mb-4">{service.description}</p>
                <div className="mb-4">
                  {service.priceNote && <span className="text-[#666] text-xs">{service.priceNote}</span>}
                  <div className="font-heading text-3xl font-bold text-[#00F0FF]">
                    ${typeof service.price === 'number' ? service.price : service.price}
                  </div>
                </div>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#888]">
                      <Check className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/"
                  className="block w-full text-center bg-[#1A1A1A] border border-white/10 text-white py-3 font-semibold uppercase tracking-wider text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition"
                >
                  Order Now
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Combo Packs */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-5xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-4">Combo Packs</h2>
          <p className="text-[#888] text-center mb-12">Save more with our bundled services</p>
          <div className="grid md:grid-cols-3 gap-6">
            {comboPacks.map((pack, idx) => (
              <div key={idx} className="relative overflow-hidden bg-gradient-to-br from-[#00F0FF]/10 to-transparent border border-[#00F0FF]/30 p-6">
                <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1">
                  SAVE ${pack.savings}
                </div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white mb-2">{pack.name}</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-[#666] line-through">${pack.originalPrice}</span>
                  <span className="font-heading text-3xl font-bold text-[#00F0FF]">${pack.price}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {pack.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#888]">
                      <Check className="w-4 h-4 text-green-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/"
                  className="block w-full text-center btn-skew bg-[#00F0FF] text-black py-3 font-bold uppercase tracking-wider text-sm hover:bg-white transition"
                >
                  <span>Get This Pack</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services Table */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white text-center mb-12">Additional Services</h2>
          <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 font-heading text-sm font-bold uppercase tracking-wider text-[#00F0FF]">Service</th>
                  <th className="text-right px-6 py-4 font-heading text-sm font-bold uppercase tracking-wider text-[#00F0FF]">Price</th>
                </tr>
              </thead>
              <tbody>
                {additionalServices.map((service, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-[#1A1A1A] transition">
                    <td className="px-6 py-4 text-white">{service.name}</td>
                    <td className="px-6 py-4 text-right font-semibold text-[#00F0FF]">
                      {typeof service.price === 'number' ? `$${service.price}` : service.price}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-[#FF003C]/10 border border-[#FF003C]/30 p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-[#FF003C] flex-shrink-0" />
              <div>
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[#FF003C] mb-2">Important Notes</h3>
                <ul className="space-y-2 text-[#FF003C]/80 text-sm">
                  <li>• All emission-related modifications are for OFF-ROAD and COMPETITION USE ONLY</li>
                  <li>• Prices are in USD and may vary based on ECU complexity</li>
                  <li>• Some vehicles may require additional work - we'll inform you before proceeding</li>
                  <li>• Free rework included if the modification doesn't work as expected</li>
                  <li>• 30-day DTC support included with all services</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">Ready to Order?</h2>
          <p className="text-[#888] mb-8">Upload your ECU file and select your services</p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Get Started →</span>
          </Link>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default PricingPage;
